import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Chess } from "chess.js";

import { StockfishAnalysisPanel } from "@/pages/analysis/StockfishAnalysisPanel";
import { formatStockfishScore } from "@/pages/analysis/engine/stockfishProtocol";
import type { StockfishPv } from "@/pages/analysis/engine/stockfishTypes";
import { formatStockfishPvSan } from "@/pages/analysis/engine/stockfishView";
import { useStockfishEvaluation } from "@/pages/analysis/engine/useStockfishEvaluation";
import { ApiError } from "@/pages/auth/api";
import { getStoredSessionToken } from "@/pages/auth/storage";
import { useAuthSession } from "@/shared/auth/useAuthSession";
import { useI18n } from "@/shared/i18n/LanguageContext";
import { normalizeReviewPly } from "@/shared/ui/reviewPly";

import { getCurrentLivestream, getLivestreamMe, submitLivestreamGuess } from "./api";
import { computeClockSkewMs, computeRemainingMs, formatCountdownSeconds } from "./clock";
import { resolveLivestreamGameResult } from "./livestreamGameResult";
import { buildLichessAnalysisUrl, resolveLivestreamDisplayFen } from "./livestreamPosition";
import { LivestreamBoard } from "./LivestreamBoard";
import { LivestreamControls } from "./LivestreamControls";
import { LivestreamMoveHistory } from "./LivestreamMoveHistory";
import { LivestreamResultFlash } from "./LivestreamResultFlash";
import { resolveLivestreamStockfishAvailability } from "./stockfishAvailability";
import type {
  LivestreamActivitySummary,
  LivestreamGuessResult,
  LivestreamPanelMode,
  LivestreamSnapshot,
  SelectedLivestreamMove,
} from "./types";

type LivestreamGuessPanelProps = {
  layout?: "home" | "room";
};

type LivestreamRailTab = "info" | "stockfish";

type DisplayLivestreamGameResult = {
  sourceGameId: string;
  score: "1-0" | "0-1" | "1/2-1/2";
  label: string;
  tone: "white" | "black" | "draw";
};

type LoadCurrentOptions = {
  forceMode?: LivestreamPanelMode;
  preserveResultForPositionId?: string;
  preserveMode?: Extract<LivestreamPanelMode, "result_correct" | "result_wrong">;
};

function isResultMode(mode: LivestreamPanelMode) {
  return mode === "result_correct" || mode === "result_wrong";
}

function isTextEditingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
}

function isEnterShortcutBlockedTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return isTextEditingTarget(target) || target.tagName === "A" || target.tagName === "BUTTON";
}

function formatLivestreamSourceName(value: string) {
  const normalized = value.trim().toLowerCase().replace(/[_\s]+/g, " ");
  const ratingRange = normalized.match(/(\d{3,4})\D+(\d{3,4})/);

  if (ratingRange) {
    return `Crowd ${ratingRange[1]}-${ratingRange[2]}`;
  }

  if (normalized.includes("hikaru")) {
    return "Hikaru Model";
  }

  if (normalized.includes("carlsen") || normalized.includes("magnus")) {
    return "Carlsen Model";
  }

  const cleanName = normalized
    .replace(/\b(source|balance|best|model|engine|bot)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleanName) {
    return "Model";
  }

  return `${cleanName
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")} Model`;
}

export function LivestreamGuessPanel({ layout = "home" }: LivestreamGuessPanelProps) {
  const authSession = useAuthSession();
  const { copy } = useI18n();
  const fetchInFlightRef = useRef(false);
  const manualPauseRef = useRef(false);
  const gameResultTimeoutRef = useRef<number | null>(null);
  const gameResultRevealTimeoutRef = useRef<number | null>(null);
  const lastGameResultKeyRef = useRef<string | null>(null);
  const lastFetchAtRef = useRef(0);
  const reviewPlyRef = useRef<number | null>(null);
  const [snapshot, setSnapshot] = useState<LivestreamSnapshot | null>(null);
  const [mode, setMode] = useState<LivestreamPanelMode>("loading");
  const [error, setError] = useState<string | null>(null);
  const [selectedMove, setSelectedMove] = useState<SelectedLivestreamMove | null>(null);
  const [result, setResult] = useState<LivestreamGuessResult | null>(null);
  const [activity, setActivity] = useState<LivestreamActivitySummary | null>(null);
  const [authPrompt, setAuthPrompt] = useState<string | null>(null);
  const [clockSkewMs, setClockSkewMs] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [reviewPly, setReviewPly] = useState<number | null>(null);
  const [frozenRemainingMs, setFrozenRemainingMs] = useState<number | null>(null);
  const [gameResultFlash, setGameResultFlash] = useState<DisplayLivestreamGameResult | null>(null);
  const [railTab, setRailTab] = useState<LivestreamRailTab>("info");
  const [stockfishExpandedPv, setStockfishExpandedPv] = useState<number | null>(null);
  const [showAllStockfishLines, setShowAllStockfishLines] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const loadCurrent = useCallback(async (options?: LoadCurrentOptions) => {
    if (fetchInFlightRef.current) {
      return;
    }
    fetchInFlightRef.current = true;
    try {
      const next = await getCurrentLivestream();
      const preserveResult =
        Boolean(options?.preserveResultForPositionId) &&
        next.position_id === options?.preserveResultForPositionId;
      manualPauseRef.current = false;
      setSnapshot(next);
      setClockSkewMs(computeClockSkewMs(next.server_now));
      setError(null);
      setSelectedMove(null);
      setResult((current) => (preserveResult ? current : null));
      setAuthPrompt(null);
      setReviewPly(null);
      setFrozenRemainingMs(null);
      lastFetchAtRef.current = Date.now();
      setMode(preserveResult ? options?.preserveMode ?? "live" : options?.forceMode ?? "live");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : copy.livestream.inactive);
      setMode("error");
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [copy.livestream.inactive]);

  useEffect(() => {
    void loadCurrent();
  }, [loadCurrent]);

  useEffect(() => {
    return () => {
      if (gameResultRevealTimeoutRef.current !== null) {
        window.clearTimeout(gameResultRevealTimeoutRef.current);
      }
      if (gameResultTimeoutRef.current !== null) {
        window.clearTimeout(gameResultTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    reviewPlyRef.current = reviewPly;
  }, [reviewPly]);

  useEffect(() => {
    if (!isHelpOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsHelpOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isHelpOpen]);

  useEffect(() => {
    if (authSession.status !== "authenticated") {
      setActivity(null);
      return;
    }
    const token = getStoredSessionToken();
    if (!token) {
      setActivity(null);
      return;
    }
    let cancelled = false;
    void getLivestreamMe(token)
      .then((next) => {
        if (!cancelled) {
          setActivity(next);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setActivity(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authSession.status, authSession.username]);

  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, []);

  const serverRemainingMs = snapshot
    ? computeRemainingMs(snapshot.position_deadline_at, clockSkewMs, nowMs)
    : 0;
  const remainingMs =
    (mode === "paused" || mode === "selected") && frozenRemainingMs !== null
      ? frozenRemainingMs
      : serverRemainingMs;

  useEffect(() => {
    if (
      !snapshot ||
      mode === "paused" ||
      mode === "selected" ||
      mode === "submitting" ||
      reviewPly !== null
    ) {
      return;
    }
    const pollMs = document.visibilityState === "hidden" ? 10000 : 1500;
    const shouldRefresh = remainingMs <= 80 || Date.now() - lastFetchAtRef.current >= pollMs;
    if (shouldRefresh) {
      void loadCurrent(
        result && isResultMode(mode)
          ? {
              preserveResultForPositionId: result.position_id,
              preserveMode: mode,
            }
          : undefined,
      );
    }
  }, [loadCurrent, mode, remainingMs, result, reviewPly, snapshot]);

  useEffect(() => {
    if (!snapshot) {
      return;
    }
    if (!selectedMove && mode === "selected") {
      setMode(manualPauseRef.current ? "paused" : "live");
    }
  }, [mode, selectedMove, snapshot]);

  const displayPly = useMemo(() => {
    if (!snapshot) {
      return 0;
    }
    return reviewPly ?? snapshot.visible_moves.length;
  }, [reviewPly, snapshot]);

  const displayFen = useMemo(
    () => (snapshot ? resolveLivestreamDisplayFen(snapshot, displayPly) : null),
    [displayPly, snapshot],
  );
  const lichessAnalysisUrl = useMemo(
    () => (displayFen ? buildLichessAnalysisUrl(displayFen) : null),
    [displayFen],
  );
  const stockfishAvailability = useMemo(
    () =>
      resolveLivestreamStockfishAvailability({
        visiblePlyCount: snapshot?.visible_moves.length ?? 0,
        displayPly,
        reviewPly,
        mode,
      }),
    [displayPly, mode, reviewPly, snapshot],
  );
  const stockfishActive =
    layout === "room" && railTab === "stockfish" && stockfishAvailability.allowed;
  const {
    enabled: stockfishEnabled,
    setEnabled: setStockfishEnabled,
    status: stockfishStatus,
    evaluation: stockfishEvaluation,
  } = useStockfishEvaluation({
    fen: displayFen,
    active: stockfishActive,
    depth: 12,
    multiPv: 5,
  });
  const stockfishTurn = useMemo(() => {
    if (!displayFen) {
      return "w" as const;
    }
    return new Chess(displayFen).turn();
  }, [displayFen]);

  const terminalResult = useMemo(
    () => {
      const derived = snapshot ? resolveLivestreamGameResult(snapshot) : null;
      if (!derived) {
        return null;
      }
      return {
        ...derived,
        label:
          derived.score === "1-0"
            ? copy.livestream.result.whiteWins
            : derived.score === "0-1"
              ? copy.livestream.result.blackWins
              : copy.livestream.result.draw,
      };
    },
    [
      copy.livestream.result.blackWins,
      copy.livestream.result.draw,
      copy.livestream.result.whiteWins,
      snapshot,
    ],
  );

  useEffect(() => {
    if (!snapshot || layout !== "room" || reviewPly !== null || !terminalResult) {
      return;
    }

    const resultKey = `${snapshot.source_set_id}:${terminalResult.sourceGameId}:${terminalResult.score}`;
    if (lastGameResultKeyRef.current === resultKey) {
      return;
    }

    lastGameResultKeyRef.current = resultKey;
    if (gameResultRevealTimeoutRef.current !== null) {
      window.clearTimeout(gameResultRevealTimeoutRef.current);
    }
    const revealDelayMs = computeRemainingMs(snapshot.position_deadline_at, clockSkewMs) + 120;
    gameResultRevealTimeoutRef.current = window.setTimeout(() => {
      if (reviewPlyRef.current !== null) {
        return;
      }
      setGameResultFlash(terminalResult);
      if (gameResultTimeoutRef.current !== null) {
        window.clearTimeout(gameResultTimeoutRef.current);
      }
      gameResultTimeoutRef.current = window.setTimeout(() => {
        setGameResultFlash(null);
      }, 4200);
    }, Math.max(0, revealDelayMs));
  }, [clockSkewMs, layout, reviewPly, snapshot, terminalResult]);

  useEffect(() => {
    setStockfishExpandedPv(null);
  }, [displayFen]);

  const statusLabel = useMemo(() => {
    if (mode === "loading") {
      return copy.livestream.statuses.syncing;
    }
    if (mode === "error") {
      return copy.livestream.statuses.retry;
    }
    if (mode === "paused" || mode === "selected") {
      return copy.livestream.statuses.paused;
    }
    if (reviewPly !== null) {
      return copy.livestream.statuses.review;
    }
    if (mode === "result_correct") {
      return copy.livestream.statuses.matched;
    }
    if (mode === "result_wrong") {
      return copy.livestream.statuses.wrong;
    }
    return copy.livestream.statuses.live;
  }, [copy.livestream.statuses, mode, reviewPly]);
  const stockfishPvs = stockfishEvaluation?.pvs ?? [];
  const stockfishPrimaryPv = stockfishPvs[0];
  const stockfishScoreLabel = !stockfishEnabled
    ? "OFF"
    : stockfishPrimaryPv
      ? formatStockfishScore(stockfishPrimaryPv, stockfishTurn)
      : stockfishStatus === "error" || stockfishStatus === "unsupported"
        ? "ERR"
        : "...";
  const visibleStockfishLineCount = showAllStockfishLines ? 5 : 3;
  const stockfishLines = stockfishEnabled
    ? Array.from({ length: visibleStockfishLineCount }, (_, index) => {
        const pv = stockfishPvs[index] as StockfishPv | undefined;
        return {
          rank: index + 1,
          eval: pv ? formatStockfishScore(pv, stockfishTurn) : "...",
          variation: pv && displayFen ? formatStockfishPvSan(displayFen, pv.pv) : "...",
          hasPv: Boolean(pv),
        };
      })
    : [];

  const onPauseToggle = () => {
    if (!snapshot || mode === "submitting" || mode === "result_correct" || mode === "result_wrong") {
      return;
    }
    if (mode === "paused") {
      void loadCurrent({ forceMode: "live" });
      return;
    }
    manualPauseRef.current = true;
    setReviewPly(null);
    setSelectedMove(null);
    setResult(null);
    setAuthPrompt(null);
    setFrozenRemainingMs(serverRemainingMs);
    setMode("paused");
  };

  const onLatest = useCallback(() => {
    void loadCurrent({ forceMode: "live" });
  }, [loadCurrent]);

  const openReviewPly = useCallback(
    (ply: number) => {
      if (!snapshot) {
        return;
      }
      const nextReviewPly = normalizeReviewPly(ply, snapshot.visible_moves.length);
      if (nextReviewPly === null) {
        onLatest();
        return;
      }
      manualPauseRef.current = false;
      setSelectedMove(null);
      setFrozenRemainingMs(null);
      setReviewPly(nextReviewPly);
      setMode("review");
    },
    [onLatest, snapshot],
  );

  const onEnd = () => {
    if (!snapshot) {
      return;
    }
    onLatest();
  };

  const onStart = () => {
    if (!snapshot) {
      return;
    }
    openReviewPly(0);
  };

  const onPrev = () => {
    if (!snapshot) {
      return;
    }
    const current = reviewPly ?? snapshot.visible_moves.length;
    openReviewPly(Math.max(0, current - 1));
  };

  const onNext = () => {
    if (!snapshot) {
      return;
    }
    const current = reviewPly ?? snapshot.visible_moves.length;
    if (current >= snapshot.visible_moves.length) {
      return;
    }
    const next = Math.min(snapshot.visible_moves.length, current + 1);
    openReviewPly(next);
  };

  const onCancelSelection = () => {
    void loadCurrent({ forceMode: "live" });
  };

  const onLockIn = async () => {
    if (!snapshot || !selectedMove) {
      return;
    }
    const token = getStoredSessionToken();
    if (!token || authSession.status !== "authenticated") {
      setAuthPrompt(copy.livestream.authPrompt);
      return;
    }
    setMode("submitting");
    setAuthPrompt(null);
    try {
      const next = await submitLivestreamGuess(token, snapshot.cursor_token, {
        guess_move: selectedMove.uci,
        submission_mode: manualPauseRef.current ? "paused" : "live",
      });
      setResult(next);
      setActivity(next.activity_summary);
      setSelectedMove(null);
      setMode(next.is_correct ? "result_correct" : "result_wrong");
    } catch (submitError) {
      const detail =
        submitError instanceof ApiError && submitError.status === 401
          ? copy.livestream.authPrompt
          : submitError instanceof Error
            ? submitError.message
            : copy.livestream.inactive;
      setAuthPrompt(detail);
      setMode("selected");
    }
  };

  const onMoveSelected = (move: SelectedLivestreamMove | null) => {
    setSelectedMove(move);
    setAuthPrompt(null);
    if (!move) {
      return;
    }
    setFrozenRemainingMs((current) => current ?? serverRemainingMs);
    setReviewPly(null);
    setMode("selected");
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!snapshot || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      if (event.key === "Enter") {
        if (isEnterShortcutBlockedTarget(event.target)) {
          return;
        }
        if (selectedMove && mode === "selected") {
          event.preventDefault();
          void onLockIn();
        }
        return;
      }
      if (!["ArrowUp", "ArrowLeft", "ArrowRight", "ArrowDown"].includes(event.key)) {
        return;
      }
      if (isTextEditingTarget(event.target)) {
        return;
      }
      event.preventDefault();
      if (event.key === "ArrowUp") {
        onStart();
      } else if (event.key === "ArrowLeft") {
        onPrev();
      } else if (event.key === "ArrowRight") {
        onNext();
      } else {
        onEnd();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [loadCurrent, mode, reviewPly, selectedMove, snapshot]);

  const renderTimer = (compact = false) => {
    const pauseDisabled =
      !snapshot ||
      mode === "selected" ||
      mode === "submitting" ||
      mode === "result_correct" ||
      mode === "result_wrong" ||
      reviewPly !== null;
    const pauseLabel =
      mode === "paused"
        ? copy.livestream.timer.latest
        : mode === "selected"
          ? copy.livestream.timer.paused
          : copy.livestream.timer.pause;

    return (
      <div className={compact ? "livestream-timer livestream-timer--compact" : "livestream-timer"}>
        <strong>{formatCountdownSeconds(remainingMs)}</strong>
        <button type="button" disabled={pauseDisabled} onClick={onPauseToggle}>
          {pauseLabel}
        </button>
      </div>
    );
  };

  if (!snapshot && mode === "loading") {
    if (layout === "room") {
      return (
        <section className="livestream-room" aria-label={copy.livestream.label}>
          <div className="livestream-room__loading" aria-busy="true">
            <span>{copy.livestream.syncing}</span>
            <strong>{copy.livestream.title}</strong>
          </div>
        </section>
      );
    }
    return (
      <section className="livestream-panel livestream-panel--loading" aria-label={copy.livestream.label}>
        <div className="livestream-panel__top">
          <h2>{copy.livestream.title}</h2>
          <span className="livestream-panel__status">{copy.livestream.syncing}</span>
        </div>
        <div className="livestream-panel__skeleton" aria-busy="true" />
      </section>
    );
  }

  if (!snapshot) {
    return (
      <section
        className={layout === "room" ? "livestream-room" : "livestream-panel livestream-panel--error"}
        aria-label={copy.livestream.label}
      >
        <div className="livestream-panel__error">
          <strong>{copy.livestream.feedUnavailable}</strong>
          <span>{error ?? copy.livestream.inactive}</span>
          <button type="button" onClick={() => loadCurrent()}>
            {copy.livestream.retry}
          </button>
        </div>
      </section>
    );
  }

  const canInteract =
    reviewPly === null &&
    mode !== "submitting" &&
    mode !== "result_correct" &&
    mode !== "result_wrong";
  const canStepBack = displayPly > 0;
  const canStepForward = displayPly < snapshot.visible_moves.length;
  const right = activity?.livestream_guessed_right ?? 0;
  const wrong = activity?.livestream_guessed_wrong ?? 0;
  const activePly = displayPly;
  const panelClassName = `livestream-panel livestream-panel--${mode}`;
  const whiteSourceName = formatLivestreamSourceName(snapshot.source.white);
  const blackSourceName = formatLivestreamSourceName(snapshot.source.black);
  const authPromptIsSignIn =
    authPrompt === copy.livestream.authPrompt ||
    authPrompt?.toLowerCase().includes("sign in") === true;
  const authPromptBlock = authPrompt ? (
    <div className="livestream-auth livestream-auth--attention" role="alert" aria-live="assertive">
      <span className="livestream-auth__message">{authPrompt}</span>
      {authPromptIsSignIn ? <Link to="/login">{copy.livestream.signIn}</Link> : null}
    </div>
  ) : null;
  const helpDialog =
    isHelpOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            className="livestream-help-backdrop"
            role="presentation"
            onClick={() => setIsHelpOpen(false)}
          >
            <section
              className="livestream-help-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="livestream-help-title"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                className="livestream-help-dialog__close"
                type="button"
                aria-label={copy.livestream.help.close}
                onClick={() => setIsHelpOpen(false)}
              >
                x
              </button>
              <span>{copy.livestream.help.eyebrow}</span>
              <h3 id="livestream-help-title">{copy.livestream.help.title}</h3>
              <p>{copy.livestream.help.body}</p>
            </section>
          </div>,
          document.body,
        )
      : null;

  const boardBlock = (
    <>
      {selectedMove ? (
        <div className="livestream-confirm" role="status">
          <p>
            <span>
              {copy.livestream.confirm.lock} {selectedMove.from} {copy.livestream.confirm.to}{" "}
              {selectedMove.to}
              {copy.livestream.confirm.question}
            </span>
            <small>{copy.livestream.confirm.enter}</small>
          </p>
          <div className="livestream-confirm__actions">
            <button type="button" onClick={onCancelSelection}>
              {copy.livestream.confirm.cancel}
            </button>
            <button type="button" disabled={mode === "submitting"} onClick={onLockIn}>
              {mode === "submitting"
                ? copy.livestream.confirm.locking
                : copy.livestream.confirm.lockIn}
            </button>
          </div>
        </div>
      ) : null}

      {layout === "home" ? authPromptBlock : null}

      <div className="livestream-panel__board-shell">
        {layout === "room" && gameResultFlash ? (
          <div
            className={`livestream-end-flash livestream-end-flash--${gameResultFlash.tone}`}
            role="status"
            aria-live="polite"
          >
            <span>{copy.livestream.result.final}</span>
            <strong>{gameResultFlash.score}</strong>
            <em>{gameResultFlash.label}</em>
          </div>
        ) : null}
        <LivestreamResultFlash result={result} />
        <LivestreamBoard
          snapshot={snapshot}
          displayPly={displayPly}
          interactive={canInteract}
          selectedMove={selectedMove}
          result={result}
          onMoveSelected={onMoveSelected}
        />
      </div>

      {layout === "room" ? authPromptBlock : null}

      <LivestreamControls
        canStepBack={canStepBack}
        canStepForward={canStepForward}
        onStart={onStart}
        onPrev={onPrev}
        onNext={onNext}
        onLatest={onLatest}
      />
    </>
  );

  if (layout === "room") {
    const stockfishLock = stockfishAvailability.allowed
      ? undefined
      : {
          title:
            stockfishAvailability.reason === "no_moves"
              ? copy.livestream.stockfishLocks.noPosition
              : stockfishAvailability.reason === "submitting"
                ? copy.livestream.stockfishLocks.lockingMove
                : copy.livestream.stockfishLocks.currentLive,
          message:
            stockfishAvailability.reason === "submitting"
              ? copy.livestream.stockfishLocks.afterGuess
              : stockfishAvailability.reason === "no_moves"
                ? copy.livestream.stockfishLocks.afterFirstMove
                : undefined,
          actionLabel:
            stockfishAvailability.reason === "current_live_position" && canStepBack
              ? copy.livestream.stockfishLocks.reviewPrevious
              : undefined,
          onAction:
            stockfishAvailability.reason === "current_live_position" && canStepBack
              ? onPrev
              : undefined,
        };
    return (
      <section className={`livestream-room livestream-room--${mode}`} aria-label={copy.livestream.label}>
        <aside className="session-rail session-rail--status livestream-room__status">
          <div className="livestream-rail-tabs" role="tablist" aria-label="Livestream rail">
            <button
              type="button"
              role="tab"
              aria-selected={railTab === "info"}
              className={railTab === "info" ? "livestream-rail-tab livestream-rail-tab--active" : "livestream-rail-tab"}
              onClick={() => setRailTab("info")}
            >
              {copy.livestream.room.info}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={railTab === "stockfish"}
              className={
                railTab === "stockfish"
                  ? "livestream-rail-tab livestream-rail-tab--active"
                  : "livestream-rail-tab"
              }
              onClick={() => setRailTab("stockfish")}
            >
              {copy.livestream.room.stockfish}
            </button>
          </div>

          {railTab === "info" ? (
            <>
              <div className="session-identity">
                <h1>{copy.livestream.title}</h1>
                <div className="livestream-room-sources" aria-label="Livestream model sources">
                  <div className="livestream-room-source" data-side="white">
                    <span>{copy.livestream.room.white}</span>
                    <strong>{whiteSourceName}</strong>
                  </div>
                  <div className="livestream-room-source" data-side="black">
                    <span>{copy.livestream.room.black}</span>
                    <strong>{blackSourceName}</strong>
                  </div>
                </div>
              </div>

              <div className={`session-state-card session-state-card--${mode === "live" ? "active" : "settled"}`}>
                <p className="session-state-card__label">{statusLabel}</p>
                {renderTimer()}
              </div>

              {authSession.status === "authenticated" ? (
                <div className="livestream-score">
                  <span>{copy.livestream.statuses.matched} {right}</span>
                  <span>{copy.livestream.statuses.wrong} {wrong}</span>
                </div>
              ) : (
                <div className="session-public-card">
                  <span>Watch mode</span>
                  <strong>Public</strong>
                </div>
              )}
            </>
          ) : (
            <div className="livestream-stockfish-panel">
              {stockfishAvailability.allowed && lichessAnalysisUrl ? (
                <a
                  className="livestream-lichess-link"
                  href={lichessAnalysisUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>{copy.livestream.room.lichess}</span>
                  <strong>{copy.livestream.room.open}</strong>
                </a>
              ) : null}
              <StockfishAnalysisPanel
                enabled={stockfishEnabled}
                status={stockfishStatus}
                scoreLabel={stockfishScoreLabel}
                lines={stockfishLines}
                expandedLine={stockfishExpandedPv}
                showAllLines={showAllStockfishLines}
                locked={stockfishLock}
                onToggleEnabled={() => setStockfishEnabled((current) => !current)}
                onToggleLine={(rank) =>
                  setStockfishExpandedPv((current) => (current === rank ? null : rank))
                }
                onToggleShowAllLines={() => setShowAllStockfishLines((current) => !current)}
              />
            </div>
          )}
        </aside>

        <div className="livestream-room__board-column">{boardBlock}</div>

        <aside className="session-rail session-rail--moves livestream-room__moves">
          <div className="session-rail__head">
            <p className="session-rail__eyebrow">{copy.livestream.room.moves}</p>
            <div className="session-review-control">
              <span className={reviewPly === null ? "session-review-pill session-review-pill--live" : "session-review-pill session-review-pill--review"}>
                {reviewPly === null ? "Live" : `View ${displayPly}`}
              </span>
              <span className="session-rail__meta">{snapshot.visible_moves.length} ply</span>
            </div>
          </div>
          <LivestreamMoveHistory
            snapshot={snapshot}
            activePly={activePly}
            onOpenPly={openReviewPly}
          />
        </aside>
      </section>
    );
  }

  return (
      <section className={panelClassName} aria-label={copy.livestream.label}>
      <div className="livestream-panel__top">
        <div className="livestream-panel__title-row">
          <h2>{copy.livestream.title}</h2>
          <button
            className="livestream-help-button"
            type="button"
            aria-label={copy.livestream.help.eyebrow}
            aria-expanded={isHelpOpen}
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
        <div className="livestream-panel__status-stack">
          <span className="livestream-panel__status">{statusLabel}</span>
          {renderTimer(true)}
        </div>
      </div>

      {helpDialog}

      {boardBlock}

      {authSession.status === "authenticated" ? (
        <div className="livestream-score">
          <span>{copy.livestream.statuses.matched} {right}</span>
          <span>{copy.livestream.statuses.wrong} {wrong}</span>
        </div>
      ) : null}
    </section>
  );
}
